import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading, false = unauthed, obj = authed
  const [ready, setReady] = useState(false);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("wardogs_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("wardogs_token");
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // Credit a streamer referral once the user is authenticated (first touch wins; backend is idempotent).
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    const raw = localStorage.getItem("ocedogs_ref");
    if (!raw) return;
    let login = null;
    try {
      const o = JSON.parse(raw);
      if (o && o.login && (!o.ts || Date.now() - o.ts < 30 * 86400000)) login = o.login;
    } catch { /* ignore malformed */ }
    if (!login) { localStorage.removeItem("ocedogs_ref"); return; }
    api.post("/referrals/streamer/attribute", { login })
      .catch(() => {})
      .finally(() => localStorage.removeItem("ocedogs_ref"));
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("wardogs_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, callsign) => {
    const { data } = await api.post("/auth/register", { email, password, callsign });
    localStorage.setItem("wardogs_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("wardogs_token");
    setUser(false);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
